import React, { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { FileText } from 'lucide-react';

/** Etichette italiane della barra del lightbox (in alto a destra). */
const ETICHETTE = {
  Close: 'Chiudi',
  Download: 'Scarica',
  'Zoom in': 'Ingrandisci',
  'Zoom out': 'Riduci',
  Lightbox: 'Visualizzatore immagine',
};

interface Props {
  url: string;
  /** URL che scarica il file invece di mostrarlo (firmato con `attachment`).
   *  Senza, "Scarica" apre `url`: succede solo nelle anteprime appena caricate. */
  downloadUrl?: string | null;
  mime?: string | null;
  name?: string | null;
  /** Cosa mostra l'immagine, per lo screen reader e il titolo. */
  alt: string;
  testid?: string;
  /** Classi della miniatura (dimensioni, bordo). */
  imgClassName?: string;
  /** Classi del link al PDF. */
  pdfClassName?: string;
  /** Contenuto del link al PDF; di default icona e nome del file. */
  pdfContent?: React.ReactNode;
}

/** Un allegato di un reso, del cliente o dell'admin.
 *
 *  Una foto è una miniatura: il clic la apre in un lightbox, con Ingrandisci,
 *  Scarica e Chiudi in alto a destra; nulla si scarica da solo. Un PDF si apre
 *  in una nuova scheda, nel visualizzatore del browser, che ha già il suo
 *  pulsante per scaricarlo. */
export function AllegatoRma({ url, downloadUrl, mime, name, alt, testid, imgClassName, pdfClassName, pdfContent }: Props) {
  const [aperto, setAperto] = useState(false);

  if (mime === 'application/pdf') {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={pdfClassName} data-testid={testid}
        title={name || 'Allegato PDF'}>
        {pdfContent ?? (<><FileText className="w-3.5 h-3.5" /> {name || 'Allegato PDF'}</>)}
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAperto(true)}
        aria-label={`Apri ${alt}`}
        title="Apri l'immagine"
        className="inline-block p-0 border-0 bg-transparent cursor-zoom-in rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(214,58%,41%)] focus-visible:ring-offset-2"
      >
        <img src={url} alt={alt} className={imgClassName} data-testid={testid} />
      </button>
      <Lightbox
        open={aperto}
        close={() => setAperto(false)}
        slides={[{ src: url, alt, download: downloadUrl || url }]}
        plugins={[Zoom, Download]}
        labels={ETICHETTE}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
        // Una foto sola: niente frecce avanti/indietro.
        render={{ buttonPrev: () => null, buttonNext: () => null }}
        zoom={{ maxZoomPixelRatio: 3, scrollToZoom: true }}
      />
    </>
  );
}
